import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { ArrowLeft, Loader2, Users } from 'lucide-react';
import {
  ZPRO_PAGE,
  ZproMensagem,
  ZproMensagemBolha,
  ZproSeparadorDia,
  ZproTicket,
  chaveDia,
  erroMsg,
  fmt,
  statusLabel,
} from './zproHistorico';

interface Props {
  open: boolean;
  onClose: () => void;
  contatoId: string | null;
  contatoNome?: string;
}

// Acesso pela lista de contatos. No atendimento, o histórico aparece direto na lista de mensagens
// (ver HistoricoZproInline).
export const HistoricoZproDialog: React.FC<Props> = ({ open, onClose, contatoId, contatoNome }) => {
  const [tickets, setTickets] = useState<ZproTicket[]>([]);
  const [ticket, setTicket] = useState<ZproTicket | null>(null);
  const [mensagens, setMensagens] = useState<ZproMensagem[]>([]);
  const [temMais, setTemMais] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !contatoId) return;
    let cancelado = false;
    setTicket(null);
    setMensagens([]);
    setErro(null);
    setLoading(true);
    api
      .get(`/contatos/${contatoId}/historico-zpro`)
      .then((r) => !cancelado && setTickets(r?.data?.data || []))
      .catch((e) => !cancelado && (setTickets([]), setErro(erroMsg(e))))
      .finally(() => !cancelado && setLoading(false));
    return () => {
      cancelado = true;
    };
  }, [open, contatoId]);

  const carregarMensagens = async (t: ZproTicket, offset: number) => {
    try {
      setLoading(true);
      setErro(null);
      const r = await api.get(`/contatos/${contatoId}/historico-zpro/tickets/${t.id}/mensagens`, {
        params: { limit: ZPRO_PAGE, offset },
      });
      const d = r?.data?.data;
      setMensagens((prev) => (offset === 0 ? d.mensagens : [...prev, ...d.mensagens]));
      setTemMais(!!d.temMais);
    } catch (e) {
      setErro(erroMsg(e));
    } finally {
      setLoading(false);
    }
  };

  const abrirTicket = (t: ZproTicket) => {
    setTicket(t);
    setMensagens([]);
    setTemMais(false);
    carregarMensagens(t, 0);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="dark:bg-slate-800 sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="dark:text-white">Histórico do Zpro{contatoNome ? ` — ${contatoNome}` : ''}</DialogTitle>
          <DialogDescription>Consulta somente leitura ao sistema anterior.</DialogDescription>
        </DialogHeader>

        {erro && <p className="text-sm text-red-600 dark:text-red-400">{erro}</p>}

        {!ticket && (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
            {loading && <p className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Carregando...</p>}
            {!loading && !erro && tickets.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400">Nenhum atendimento encontrado no Zpro para este contato.</p>
            )}
            {tickets.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => abrirTicket(t)}
                className="w-full text-left rounded-md border border-gray-200 dark:border-slate-600 p-3 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium dark:text-white flex items-center gap-1">
                    {t.grupo && <Users className="h-3.5 w-3.5" />}
                    {t.protocolo ? `Protocolo ${t.protocolo}` : `Ticket #${t.id}`}
                  </span>
                  <span className="text-xs rounded-full px-2 py-0.5 bg-gray-100 dark:bg-slate-600 dark:text-gray-200">
                    {statusLabel[t.status] || t.status}
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {fmt(t.criadoEm)}
                  {t.fechadoEm ? ` → ${fmt(t.fechadoEm)}` : ''} · {t.totalMensagens} msg
                  {t.atendente ? ` · ${t.atendente}` : ''}
                  {t.fila ? ` · ${t.fila}` : ''}
                </p>
                {t.ultimaMensagem && <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 truncate">{t.ultimaMensagem}</p>}
              </button>
            ))}
          </div>
        )}

        {ticket && (
          <div className="space-y-3">
            <Button variant="ghost" size="sm" onClick={() => setTicket(null)} className="dark:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" /> Voltar aos atendimentos
            </Button>
            <div className="max-h-[55vh] overflow-y-auto space-y-2 pr-1">
              {mensagens.map((m, i) => (
                <React.Fragment key={m.id}>
                  {(i === 0 || chaveDia(mensagens[i - 1].criadaEm) !== chaveDia(m.criadaEm)) && (
                    <ZproSeparadorDia iso={m.criadaEm} />
                  )}
                  <ZproMensagemBolha m={m} />
                </React.Fragment>
              ))}
              {loading && <p className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Carregando...</p>}
              {!loading && mensagens.length === 0 && !erro && <p className="text-sm text-gray-500">Sem mensagens neste atendimento.</p>}
              {temMais && !loading && (
                <div className="text-center">
                  <Button variant="outline" size="sm" onClick={() => carregarMensagens(ticket, mensagens.length)} className="dark:text-white dark:bg-slate-700">
                    Carregar mais
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HistoricoZproDialog;
